import type { ApiResponse } from '~/types/api';

/**
 * API istekleri için temel servis.
 * Tüm API istekleri bu servis üzerinden yapılmalıdır.
 */
export default class ApiService {
  private static instance: ApiService;
  private baseUrl: string;

  private constructor() {
    // Nuxt'un ortam değişkenlerini kullan
    const config = useRuntimeConfig()
    this.baseUrl = config.public.NUXT_PUBLIC_BACKEND_BASE_URL || 'http://localhost:8080';
  }

  /**
   * Singleton örneği al
   */
  public static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  /**
   * Geçerli dili alma fonksiyonu
   */
  private getCurrentLang(): string {
    // Tarayıcı ortamında isek
    if (typeof window !== 'undefined') {
      // URL'den dil kodu almaya çalış (örn: /tr/haberler)
      const pathParts = window.location.pathname.split('/').filter(p => p);
      const firstPart = pathParts[0];
      
      // İlk parça dil kodu ise (tr veya en)
      if (['tr', 'en'].includes(firstPart)) {
        return firstPart;
      }
    }
    
    // Varsayılan olarak Türkçe kullan
    return 'tr';
  }

  /**
   * GET isteği gönder
   * @param endpoint API endpoint
   * @param params Sorgu parametreleri
   */
  public async get<T>(endpoint: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    try {
      console.log(`API GET isteği başlatılıyor: ${endpoint}`, params);
      
      // Her API isteğine otomatik olarak dil parametresi ekle
      const currentLang = this.getCurrentLang();
      const paramsWithLang = { ...(params || {}), lang: currentLang };
      
      const url = new URL(`${this.baseUrl}${endpoint}`);
      
      // Parametreleri URL'ye ekle - undefined değerleri kontrol ederek
      if (paramsWithLang) {
        Object.entries(paramsWithLang).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            url.searchParams.append(key, String(value));
          }
        });
      }
      
      console.log(`API isteği için URL: ${url.toString()}`);
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      console.log(`API GET yanıtı alındı: ${endpoint}`, data);
      return data as ApiResponse<T>;
    } catch (error) {
      console.error('API GET isteği sırasında hata oluştu:', error);
      throw error;
    }
  }

  /**
   * POST isteği gönder
   * @param endpoint API endpoint
   * @param body Gönderilecek veri
   */
  public async post<T>(endpoint: string, body: any): Promise<ApiResponse<T>> {
    try {
      // Her API isteğine otomatik olarak dil parametresi ekle
      const currentLang = this.getCurrentLang();
      const bodyWithLang = { ...(body || {}), lang: currentLang };
      
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bodyWithLang)
      });
      
      const data = await response.json();
      return data as ApiResponse<T>;
    } catch (error) {
      console.error('API POST isteği sırasında hata oluştu:', error);
      throw error;
    }
  }

  /**
   * PUT isteği gönder
   * @param endpoint API endpoint
   * @param body Gönderilecek veri
   */
  public async put<T>(endpoint: string, body: any): Promise<ApiResponse<T>> {
    try {
      // Her API isteğine otomatik olarak dil parametresi ekle
      const currentLang = this.getCurrentLang();
      const bodyWithLang = { ...(body || {}), lang: currentLang };
      
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'PUT',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bodyWithLang)
      });
      
      const data = await response.json();
      return data as ApiResponse<T>;
    } catch (error) {
      console.error('API PUT isteği sırasında hata oluştu:', error);
      throw error;
    }
  }

  /**
   * DELETE isteği gönder
   * @param endpoint API endpoint
   */
  public async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      return data as ApiResponse<T>;
    } catch (error) {
      console.error('API DELETE isteği sırasında hata oluştu:', error);
      throw error;
    }
  }
} 